'use client';

import { useState, useEffect } from 'react';
import { NEXT_PUBLIC_TRUSTI_ENV } from '@/lib/env.public';

interface DebugInfo {
  user: {
    email?: string;
    userId?: string;
    firstName?: string;
    lastName?: string;
    salesforceId?: string;
    roles?: string[];
    emailVerified?: boolean;
    isAdmin?: boolean;
  } | null;
  environment: {
    nodeEnv: string;
    trustiEnv: string | undefined;
    isDevelopment: boolean;
  };
  timestamp: string;
  url: {
    pathname: string;
    search: string;
    href: string;
  };
  viewport: {
    width: number;
    height: number;
  };
}

export function DebugButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Only render in development
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (!isDevelopment) return;

    // Fetch initial debug info
    const fetchDebugInfo = async () => {
      setIsLoading(true);
      try {
        // Fetch current user data
        const userResponse = await fetch('/api/user/current').catch(() => null);
        const userData = userResponse?.ok ? await userResponse.json() : null;

        const info: DebugInfo = {
          environment: {
            isDevelopment,
            nodeEnv: process.env.NODE_ENV,
            trustiEnv: NEXT_PUBLIC_TRUSTI_ENV,
          },
          timestamp: new Date().toISOString(),
          url: {
            href: window.location.href,
            pathname: window.location.pathname,
            search: window.location.search,
          },
          user: userData?.user || null,
          viewport: {
            height: window.innerHeight,
            width: window.innerWidth,
          },
        };

        setDebugInfo(info);
      } catch (error) {
        console.error('Failed to fetch debug info:', error);
      }
      setIsLoading(false);
    };

    if (isOpen) {
      fetchDebugInfo();
    }
  }, [isOpen, isDevelopment]);

  // Don't render anything in production
  if (!isDevelopment) {
    return null;
  }

  return (
    <>
      {/* Debug Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed right-4 bottom-4 z-[9999] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-pink-600 font-mono text-xs font-bold text-white shadow-lg transition-all hover:scale-110 hover:shadow-xl"
        title="Toggle Debug Info"
      >
        🐛
      </button>

      {/* Debug Popup */}
      {isOpen && (
        <div className="fixed right-4 bottom-20 z-[9998] max-h-[70vh] w-96 max-w-md overflow-auto rounded-lg border-2 border-purple-500 bg-white p-4 shadow-2xl dark:bg-gray-900">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              🐛 Debug Info
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          {isLoading ? (
            <div className="py-4 text-center">Loading debug info...</div>
          ) : debugInfo ? (
            <div className="space-y-4 text-sm">
              {/* Environment Section */}
              <section>
                <h4 className="mb-2 font-semibold text-gray-700 dark:text-gray-300">
                  Environment
                </h4>
                <div className="space-y-1 rounded bg-gray-50 p-2 font-mono text-xs dark:bg-gray-800">
                  <div>
                    <span className="text-gray-500">NODE_ENV:</span>{' '}
                    <span className="text-purple-600">
                      {debugInfo.environment.nodeEnv}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">TRUSTI_ENV:</span>{' '}
                    <span className="text-purple-600">
                      {debugInfo.environment.trustiEnv || 'not set'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Is Dev:</span>{' '}
                    <span className="text-purple-600">
                      {debugInfo.environment.isDevelopment ? '✅' : '❌'}
                    </span>
                  </div>
                </div>
              </section>

              {/* User Section */}
              <section>
                <h4 className="mb-2 font-semibold text-gray-700 dark:text-gray-300">
                  User
                </h4>
                {debugInfo.user ? (
                  <div className="space-y-1 rounded bg-gray-50 p-2 font-mono text-xs dark:bg-gray-800">
                    <div>
                      <span className="text-gray-500">Email:</span>{' '}
                      <span className="text-blue-600">
                        {debugInfo.user.email}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Name:</span>{' '}
                      <span className="text-blue-600">
                        {debugInfo.user.firstName} {debugInfo.user.lastName}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">User ID:</span>{' '}
                      <span className="break-all text-blue-600">
                        {debugInfo.user.userId}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Salesforce ID:</span>{' '}
                      <span className="text-blue-600">
                        {debugInfo.user.salesforceId || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Roles:</span>{' '}
                      <span className="text-blue-600">
                        {debugInfo.user.roles?.length
                          ? debugInfo.user.roles.join(', ')
                          : 'None'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Email Verified:</span>{' '}
                      <span className="text-blue-600">
                        {debugInfo.user.emailVerified ? '✅' : '❌'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Is Admin:</span>{' '}
                      <span className="text-blue-600">
                        {debugInfo.user.isAdmin ? '✅' : '❌'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="rounded bg-gray-50 p-2 text-gray-500 dark:bg-gray-800">
                    Not logged in
                  </div>
                )}
              </section>

              {/* URL Section */}
              <section>
                <h4 className="mb-2 font-semibold text-gray-700 dark:text-gray-300">
                  URL
                </h4>
                <div className="space-y-1 rounded bg-gray-50 p-2 font-mono text-xs dark:bg-gray-800">
                  <div>
                    <span className="text-gray-500">Path:</span>{' '}
                    <span className="text-green-600">
                      {debugInfo.url.pathname}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Query:</span>{' '}
                    <span className="text-green-600">
                      {debugInfo.url.search || 'none'}
                    </span>
                  </div>
                </div>
              </section>

              {/* Viewport Section */}
              <section>
                <h4 className="mb-2 font-semibold text-gray-700 dark:text-gray-300">
                  Viewport
                </h4>
                <div className="rounded bg-gray-50 p-2 font-mono text-xs dark:bg-gray-800">
                  <span className="text-gray-500">Size:</span>{' '}
                  <span className="text-orange-600">
                    {debugInfo.viewport.width} x {debugInfo.viewport.height}
                  </span>
                </div>
              </section>

              {/* Timestamp */}
              <section>
                <div className="border-t border-gray-200 pt-2 text-center text-xs text-gray-400 dark:border-gray-700">
                  Last updated:{' '}
                  {new Date(debugInfo.timestamp).toLocaleTimeString()}
                </div>
              </section>

              {/* Refresh Button */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  setTimeout(() => setIsOpen(true), 100);
                }}
                className="w-full rounded-md bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2 text-sm font-medium text-white transition-all hover:from-purple-700 hover:to-pink-700"
              >
                Refresh Debug Info
              </button>
            </div>
          ) : (
            <div className="py-4 text-center text-gray-500">
              Failed to load debug info
            </div>
          )}
        </div>
      )}
    </>
  );
}
